import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, Chip, Paper } from '@material-ui/core';
import LinearLoader from '../../components/LinearLoader';
import Notification from '../../components/Notification';
import DoctariModal from '../../components/Modal';
import { userActionsTypes } from '../../redux/user/actions';
import { CHECKOUT } from '../../utils/urls';
import { getStoredSubscription } from '../../utils/subscriptionStorage';
import {
  canCancelSubscription,
  canReactivateSubscription,
  extractSubscriptions,
  formatSubscriptionDate,
  getPlanName,
  getPlanPriceLabel,
  getStatusColor,
  getStatusKey,
  getStatusLabel,
  getSubscriptionPk,
} from '../../utils/subscriptionHelpers';
import { useStyles } from './style';

function Subscriptions({
  userState,
  getMySubscription,
  cancelSubscription,
  reactivateSubscription,
  deleteMessages,
}) {
  const classes = useStyles();
  const history = useHistory();
  const [confirmCancel, setConfirmCancel] = useState(null);

  const subscriptions = extractSubscriptions(
    userState.subscription || getStoredSubscription()
  );

  useEffect(() => {
    getMySubscription();
  }, []);

  const handleCancel = () => {
    if (!confirmCancel) {
      return;
    }
    cancelSubscription(confirmCancel);
    setConfirmCancel(null);
  };

  return (
    <>
      {userState.loading && <LinearLoader />}
      {userState.success && (
        <Notification
          type="success"
          message={userState.success}
          onCloseCallback={deleteMessages}
        />
      )}
      {userState.error && (
        <Notification
          type="error"
          message={userState.error}
          onCloseCallback={deleteMessages}
        />
      )}
      <div className={classes.page}>
        <div className={classes.content}>
          <h1 className={classes.title}>Mi suscripción</h1>
          <p className={classes.subtitle}>
            Consulta el estado de tu plan y administra la renovación.
          </p>

          {!userState.loading && subscriptions.length === 0 && (
            <Paper elevation={3} className={classes.emptyCard}>
              <p className={classes.emptyText}>
                Todavía no tienes una suscripción activa.
              </p>
              <Button
                variant="contained"
                color="primary"
                className={classes.primaryButton}
                onClick={() => history.push(CHECKOUT)}
              >
                Ver planes
              </Button>
            </Paper>
          )}

          {subscriptions.map((subscription) => {
            const statusKey = getStatusKey(subscription);
            const pk = getSubscriptionPk(subscription);
            const priceLabel = getPlanPriceLabel(subscription);
            const periodEnd = formatSubscriptionDate(
              subscription.current_period_end
            );
            const createdAt = formatSubscriptionDate(subscription.created_at);

            return (
              <Paper
                elevation={3}
                className={classes.card}
                key={pk || getPlanName(subscription)}
              >
                <div className={classes.cardHeader}>
                  <h2 className={classes.planName}>
                    {getPlanName(subscription)}
                  </h2>
                  <Chip
                    className={classes.statusChip}
                    label={getStatusLabel(statusKey)}
                    style={{ backgroundColor: getStatusColor(statusKey) }}
                  />
                </div>
                {priceLabel && <p className={classes.price}>{priceLabel}</p>}
                <div className={classes.details}>
                  {createdAt && (
                    <div className={classes.detailRow}>
                      <span className={classes.detailLabel}>Inicio</span>
                      <span className={classes.detailValue}>{createdAt}</span>
                    </div>
                  )}
                  {periodEnd && (
                    <div className={classes.detailRow}>
                      <span className={classes.detailLabel}>
                        {subscription.cancel_at_period_end
                          ? 'Se cancela el'
                          : 'Próxima renovación'}
                      </span>
                      <span className={classes.detailValue}>{periodEnd}</span>
                    </div>
                  )}
                </div>
                <div className={classes.actions}>
                  {canCancelSubscription(subscription) && (
                    <Button
                      variant="outlined"
                      color="primary"
                      className={classes.primaryButton}
                      disabled={!pk || userState.loading}
                      onClick={() => setConfirmCancel(pk)}
                    >
                      Cancelar suscripción
                    </Button>
                  )}
                  {canReactivateSubscription(subscription) && (
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.primaryButton}
                      disabled={!pk || userState.loading}
                      onClick={() => reactivateSubscription(pk)}
                    >
                      Reactivar suscripción
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    color="primary"
                    className={classes.primaryButton}
                    onClick={() => history.push(CHECKOUT)}
                  >
                    Cambiar plan
                  </Button>
                </div>
              </Paper>
            );
          })}
        </div>
      </div>
      <DoctariModal
        open={Boolean(confirmCancel)}
        setOpen={(open) => {
          if (!open) setConfirmCancel(null);
        }}
        title="Cancelar suscripción"
      >
        <p className={classes.modalBody}>
          La suscripción se cancelará al final del período actual y no se
          renovará de forma automática.
        </p>
        <div className={classes.modalActions}>
          <Button
            variant="outlined"
            color="primary"
            className={classes.primaryButton}
            onClick={() => setConfirmCancel(null)}
          >
            Volver
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.primaryButton}
            onClick={handleCancel}
          >
            Confirmar cancelación
          </Button>
        </div>
      </DoctariModal>
    </>
  );
}

const mapStateToProps = ({ user }) => {
  return {
    userState: user,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getMySubscription: () =>
    dispatch({ type: userActionsTypes.GET_MY_SUBSCRIPTION }),
  cancelSubscription: (id) =>
    dispatch({
      type: userActionsTypes.CANCEL_SUBSCRIPTION,
      payload: { id },
    }),
  reactivateSubscription: (id) =>
    dispatch({
      type: userActionsTypes.REACTIVATE_SUBSCRIPTION,
      payload: { id },
    }),
  deleteMessages: () =>
    dispatch({
      type: userActionsTypes.DELETE_MESSAGES,
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Subscriptions);
